import { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import StatCard from '@/components/base/StatCard';
import ExportMenu from '@/components/feature/ExportMenu';
import { buildCsv } from '@/utils/export';
import { useRole } from '@/context/RoleContext';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/utils/api';
import {
  filterStaff,
  isLastAdmin,
  roleHeadcount,
  staffTotals,
} from '@/utils/staff';
import type { RoleRecord, StaffMember } from '@/types/portal';
import UserToolbar from './components/UserToolbar';
import UserTable from './components/UserTable';
import InviteUserModal from './components/InviteUserModal';
import PendingInvitesPanel from './components/PendingInvitesPanel';
import RoleCoveragePanel from './components/RoleCoveragePanel';
import ChangeRoleModal from './components/ChangeRoleModal';

export default function Users() {
  const { can } = useRole();
  const canManage = can('staff.manage');
  const { user } = useAuth();
  const currentUserId = user?.id ?? '';

  const [members, setMembers] = useState<StaffMember[]>([]);
  const [roleRecords, setRoleRecords] = useState<RoleRecord[]>([]);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [resent, setResent] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Promise.all([api<StaffMember[]>('/staff'), api<RoleRecord[]>('/roles').catch(() => [] as RoleRecord[])])
      .then(([staff, roles]) => {
        setMembers(staff);
        setRoleRecords(roles);
      })
      .catch((error: Error) => setLoadError(error.message));
  }, []);

  // Errors from buttons without a popup show in a banner scrolled into view.
  const showError = (message: string) => {
    setActionError(message);
    window.setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };
  const [status, setStatus] = useState('All');
  const [roleId, setRoleId] = useState('All roles');
  const [markets, setMarkets] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [twoFactorOnly, setTwoFactorOnly] = useState(false);
  // Laravel sends no invite emails, so there are no resends to count.
  const resendCounts: Record<string, number> = {};
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteDone, setInviteDone] = useState(false);
  const [invitedName, setInvitedName] = useState('');
  const [invitedEmail, setInvitedEmail] = useState('');
  const [confirmTarget, setConfirmTarget] = useState<StaffMember | null>(null);
  const [notice, setNotice] = useState('');
  const [roleTarget, setRoleTarget] = useState<StaffMember | null>(null);
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleError, setRoleError] = useState('');

  const baseFiltered = useMemo(
    () => filterStaff(members, { status: 'All', roleId, markets, query }),
    [members, roleId, markets, query],
  );

  const tabs = useMemo(
    () => [
      { id: 'All', label: 'All', count: baseFiltered.length },
      { id: 'Active', label: 'Active', count: baseFiltered.filter((m) => m.status === 'Active').length },
      { id: 'Invited', label: 'Invited', count: baseFiltered.filter((m) => m.status === 'Invited').length },
      {
        id: 'Deactivated',
        label: 'Deactivated',
        count: baseFiltered.filter((m) => m.status === 'Deactivated').length,
      },
    ],
    [baseFiltered],
  );

  const filtered = useMemo(
    () =>
      (status === 'All' ? baseFiltered : baseFiltered.filter((m) => m.status === status)).filter(
        (m) => (twoFactorOnly ? m.twoFactor : true),
      ),
    [baseFiltered, status, twoFactorOnly],
  );

  const handleStatus = (value: string) => {
    setStatus(value);
    setTwoFactorOnly(false);
  };

  const toggleStatusCard = (target: string) => {
    setStatus((prev) => (prev === target ? 'All' : target));
    setTwoFactorOnly(false);
  };

  const toggleTwoFactorCard = () => {
    setTwoFactorOnly((prev) => !prev);
    setStatus('All');
  };

  const totals = staffTotals(members);
  const invites = members.filter((member) => member.status === 'Invited');
  const coverage = roleHeadcount(roleRecords, members);

  const openInvite = () => {
    setInviteDone(false);
    setResent(false);
    setTemporaryPassword('');
    setActionError('');
    setInvitedName('');
    setInvitedEmail('');
    setInviteOpen(true);
  };

  // StaffController::store: the user is created with a temporary password (no invite email).
  // Markets are chosen on the screen but production doesn't store them.
  const confirmInvite = async (name: string, email: string, inviteRoleId: string, _markets: string[]) => {
    setActionError('');
    try {
      const result = await api<{ members: StaffMember[]; temporaryPassword: string }>('/staff', {
        method: 'POST',
        body: JSON.stringify({ name, email, roleId: inviteRoleId }),
      });
      setMembers(result.members);
      setTemporaryPassword(result.temporaryPassword);
      setInvitedName(name);
      setInvitedEmail(email);
      setInviteDone(true);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not add the user.');
    }
  };

  const resendInvite = async (member: StaffMember) => {
    try {
      // No email is sent (production has none): the API replaces the temporary
      // password and returns it, so it must be shown here or it's lost.
      const result = await api<{ temporaryPassword?: string } | null>(`/staff/${member.id}/resend`, { method: 'POST' });
      if (result?.temporaryPassword) {
        setTemporaryPassword(result.temporaryPassword);
        setInvitedName(member.name);
        setInvitedEmail(member.email);
        setResent(true);
        setInviteDone(true);
        setInviteOpen(true);
      } else {
        setNotice(`Invitation re-sent to ${member.email}.`);
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Could not resend the invitation.');
    }
  };

  // Revoking an invite removes the user (StaffController::destroy).
  const revokeInvite = async (member: StaffMember) => {
    try {
      setMembers(await api<StaffMember[]>(`/staff/${member.id}`, { method: 'DELETE' }));
      setNotice(`Invitation to ${member.email} was revoked.`);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Could not revoke the invitation.');
    }
  };

  const applyStatus = async (member: StaffMember, next: StaffMember['status']) => {
    await api(`/staff/${member.id}/status`, { method: 'POST', body: JSON.stringify({ status: next }) });
    setNotice(
      next === 'Active'
        ? `${member.name} was reactivated.`
        : `${member.name} was deactivated and signed out everywhere.`,
    );
  };

  const requestToggle = (member: StaffMember) => {
    if (member.status === 'Active' && isLastAdmin(members, member.id)) {
      setNotice('You cannot deactivate the only active Agency Administrator.');
      return;
    }
    setConfirmTarget(member);
  };

  const confirmToggle = async () => {
    if (!confirmTarget) return;
    setConfirmError('');
    try {
      await applyStatus(confirmTarget, confirmTarget.status === 'Active' ? 'Deactivated' : 'Active');
      setConfirmTarget(null);
    } catch (error) {
      setConfirmError(error instanceof Error ? error.message : 'Could not change the status.');
    }
  };

  const openChangeRole = (member: StaffMember) => {
    setRoleError('');
    setRoleTarget(member);
  };

  // RoleController::assign replaces the user's role; the staff list is reloaded for the new name.
  const saveRole = async (member: StaffMember, nextRoleId: string) => {
    setRoleSaving(true);
    setRoleError('');
    try {
      await api(`/roles/${nextRoleId}/assign`, { method: 'POST', body: JSON.stringify({ userId: member.id }) });
      setMembers(await api<StaffMember[]>('/staff'));
      const roleName = roleRecords.find((role) => role.id === nextRoleId)?.name ?? 'the new role';
      setNotice(`${member.name} is now ${roleName}.`);
      setRoleTarget(null);
    } catch (error) {
      setRoleError(error instanceof Error ? error.message : 'Could not change the role.');
    } finally {
      setRoleSaving(false);
    }
  };

  const adminCount = members.filter((member) => member.roleId === 'admin').length;

  const buildStaffCsv = () =>
    buildCsv(
      ['Name', 'Email', 'Role', 'Markets', 'Status', '2FA', 'Last active'],
      filtered.map((member) => [
        member.name,
        member.email,
        member.roleName,
        member.markets.join(' / '),
        member.status,
        member.twoFactor ? 'Yes' : 'No',
        member.lastActive,
      ]),
    );

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <p className="text-xs font-label font-medium uppercase tracking-wider text-foreground-400">
            Settings
          </p>
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Users
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Who can sign in, their role and their markets.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportMenu
            filename="twiva-users"
            pdfTitle="Users"
            label="Export users"
            buildCsv={buildStaffCsv}
            onExported={(format) =>
              setNotice(
                format === 'csv'
                  ? `CSV exported — ${filtered.length} user${filtered.length === 1 ? '' : 's'}.`
                  : 'PDF ready — choose "Save as PDF" in the print dialog.',
              )
            }
          />
          <Button icon="ri-user-add-line" disabled={!canManage} onClick={openInvite}>
            Invite user
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total users"
          value={String(totals.total)}
          icon="ri-team-line"
          tone="primary"
          hint={`${totals.active} active · ${totals.deactivated} deactivated`}
        />
        <StatCard
          label="Active users"
          value={String(totals.active)}
          icon="ri-user-follow-line"
          tone="accent"
          hint="Can sign in right now"
          onClick={() => toggleStatusCard('Active')}
          active={status === 'Active' && !twoFactorOnly}
        />
        <StatCard
          label="Pending invites"
          value={String(totals.invited)}
          icon="ri-mail-send-line"
          tone="secondary"
          hint="Awaiting acceptance"
          onClick={() => toggleStatusCard('Invited')}
          active={status === 'Invited' && !twoFactorOnly}
        />
        <StatCard
          label="With 2FA"
          value={`${totals.twoFactorCount}/${totals.total}`}
          icon="ri-shield-check-line"
          tone="neutral"
          hint={`${totals.rolesInUse} distinct roles in use`}
          onClick={toggleTwoFactorCard}
          active={twoFactorOnly}
        />
      </div>

      {loadError ? (
        <div className="mt-5 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800">
          <i className="ri-error-warning-line mr-1" />
          {loadError}
        </div>
      ) : null}

      {actionError && !inviteOpen ? (
        <div
          ref={errorRef}
          role="alert"
          className="mt-5 flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800"
        >
          <span className="inline-flex items-center gap-2">
            <i className="ri-error-warning-line" />
            {actionError}
          </span>
          <button
            type="button"
            onClick={() => setActionError('')}
            className="cursor-pointer text-primary-700 hover:text-primary-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {notice ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {!canManage ? (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
          <i className="ri-lock-line" />
          Your current role can view users but not manage them. Switch to Administrator to invite or
          deactivate staff.
        </div>
      ) : null}

      <div className="mt-6">
        <UserToolbar
          tabs={tabs}
          status={status}
          onStatus={handleStatus}
          roleId={roleId}
          onRole={setRoleId}
          roleRecords={roleRecords}
          markets={markets}
          onSetMarkets={setMarkets}
          query={query}
          onQuery={setQuery}
          twoFactorOnly={twoFactorOnly}
          twoFactorCount={totals.twoFactorCount}
          onToggleTwoFactor={toggleTwoFactorCard}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <h3 className="font-heading text-sm font-semibold text-foreground-950">Staff &amp; access</h3>
            <span className="text-xs text-foreground-500">
              Showing {filtered.length} of {totals.total}
            </span>
          </div>
          <UserTable
            rows={filtered}
            canManage={canManage}
            currentUserId={currentUserId}
            onToggleStatus={requestToggle}
            onResend={(member) => void resendInvite(member)}
            onChangeRole={openChangeRole}
          />
        </div>

        <div className="sticky top-6 space-y-5">
          <PendingInvitesPanel
            invites={invites}
            canManage={canManage}
            resendCounts={resendCounts}
            onResend={(member) => void resendInvite(member)}
            onRevoke={(member) => void revokeInvite(member)}
          />
          <RoleCoveragePanel rows={coverage} total={totals.active} />
        </div>
      </div>

      <InviteUserModal
        open={inviteOpen}
        roles={roleRecords}
        onClose={() => setInviteOpen(false)}
        onInvite={(name, email, inviteRoleId, markets) => void confirmInvite(name, email, inviteRoleId, markets)}
        done={inviteDone}
        invitedName={invitedName}
        invitedEmail={invitedEmail}
        temporaryPassword={temporaryPassword}
        resent={resent}
        error={actionError}
      />

      <ChangeRoleModal
        member={roleTarget}
        roles={roleRecords}
        lastAdmin={roleTarget?.roleId === 'admin' && adminCount <= 1}
        saving={roleSaving}
        error={roleError}
        onClose={() => setRoleTarget(null)}
        onSave={(member, nextRoleId) => void saveRole(member, nextRoleId)}
      />

      <Modal
        open={confirmTarget !== null}
        onClose={() => {
          setConfirmTarget(null);
          setConfirmError('');
        }}
        icon={confirmTarget?.status === 'Active' ? 'ri-user-unfollow-line' : 'ri-user-follow-line'}
        title={confirmTarget?.status === 'Active' ? 'Deactivate user' : 'Reactivate user'}
        subtitle={confirmTarget?.name}
        widthClassName="max-w-md"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmTarget(null);
                setConfirmError('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant={confirmTarget?.status === 'Active' ? 'primary' : 'accent'}
              icon={confirmTarget?.status === 'Active' ? 'ri-user-unfollow-line' : 'ri-user-follow-line'}
              onClick={() => void confirmToggle()}
            >
              {confirmTarget?.status === 'Active' ? 'Deactivate' : 'Reactivate'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          {confirmTarget?.status === 'Active'
            ? `${confirmTarget?.name} will lose access immediately and be signed out on every device. Their history and audit trail stay intact.`
            : `${confirmTarget?.name} will be able to sign in again with their existing role and market access.`}
        </p>
        {confirmError ? (
          <div
            role="alert"
            className="mt-3 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800"
          >
            <i className="ri-error-warning-line mt-0.5" />
            <span>{confirmError}</span>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}